import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { registerAppDialogHandler } from '../utils/appDialog';
import './DialogContext.css';

/**
 * Hosts in-app alert/confirm dialogs for the imperative appAlert / appConfirm APIs.
 */
export function DialogProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const resolveRef = useRef(null);
  const busyRef = useRef(false);
  const pendingRef = useRef([]);

  const finish = useCallback((result) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setDialog(null);
    busyRef.current = false;
    resolve?.(result);
    const next = pendingRef.current.shift();
    if (next) {
      // Defer so React can unmount the previous dialog before opening the next.
      queueMicrotask(() => {
        busyRef.current = true;
        resolveRef.current = next.resolve;
        setDialog(next.payload);
      });
    }
  }, []);

  const present = useCallback((job) => {
    const { resolve, reject, ...payload } = job;
    if (busyRef.current) {
      pendingRef.current.push({ resolve, reject, payload });
      return Promise.resolve();
    }
    busyRef.current = true;
    resolveRef.current = resolve;
    setDialog(payload);
    return Promise.resolve();
  }, []);

  useEffect(() => {
    registerAppDialogHandler(present);
    return () => registerAppDialogHandler(null);
  }, [present]);

  useEffect(() => {
    if (!dialog) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        finish(dialog.kind === 'confirm' ? false : undefined);
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [dialog, finish]);

  return (
    <>
      {children}
      {dialog
        ? createPortal(
            <div
              className="modal-overlay app-dialog-overlay"
              role="presentation"
              onClick={() => finish(dialog.kind === 'confirm' ? false : undefined)}
            >
              <div
                className={`modal-content app-dialog ${dialog.danger ? 'app-dialog--danger' : ''}`}
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="app-dialog-title"
                aria-describedby="app-dialog-message"
                onClick={(e) => e.stopPropagation()}
              >
                <h2 id="app-dialog-title" className="app-dialog__title">
                  {dialog.title}
                </h2>
                <p id="app-dialog-message" className="app-dialog__message">
                  {dialog.message}
                </p>
                <div className="modal-actions app-dialog__actions">
                  {dialog.kind === 'confirm' ? (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => finish(false)}
                      autoFocus={Boolean(dialog.danger)}
                    >
                      {dialog.cancelLabel}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className={`btn ${dialog.danger ? 'btn-danger' : 'btn-primary'}`}
                    onClick={() => finish(dialog.kind === 'confirm' ? true : undefined)}
                    autoFocus={dialog.kind === 'alert' || (dialog.kind === 'confirm' && !dialog.danger)}
                  >
                    {dialog.confirmLabel}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
